import { toast, Flip } from 'react-toastify'
import type { ToastPosition, ToastOptions } from 'react-toastify'
import cx from 'classnames'
import * as styles from '@src/styles/index.styl'
import * as React from 'react'
import { isEmpty } from 'lodash'

interface Error {
  dev_details?: string
  user_message?: string
}

/*
 * toastError is used for displaying error messages across the app
 * Usage toastError('Invalid value from user')
 * However for backendError used in reducers please use
 * toastError(jsonify(backendError))
 */

export const getMessage = (message: string, title?: string, icon?: string) => (
  <div>
    {title && (
      <h6 className={styles.title} role="title">
        {icon && <i className={'material-icons'}>{icon}</i>}
        {title}
      </h6>
    )}
    <div className={cx(styles.body, styles.toastMessage)}>
      <p role="message">{message}</p>
    </div>
  </div>
)

export function toastError(
  message: string,
  options: ToastOptions = {
    position: toast.POSITION.TOP_RIGHT as ToastPosition,
    transition: Flip,
    hideProgressBar: true,
    pauseOnHover: true
  }
) {
  toast.dismiss()
  toast.error(getMessage(message, 'Error', 'cancel'), options)
}

/**
 * toastSuccess is used to display success messages across the app.
 * This shows a green message toaster which is fleeting. It is dismissed
 * in 2 seconds.
 * Usage toastSuccess('Success') or toastSuccess('Monitor is created successfully')
 */
export function toastSuccess(
  message: string,
  options: ToastOptions = {
    position: toast.POSITION.TOP_RIGHT as ToastPosition,
    autoClose: 2000,
    transition: Flip,
    hideProgressBar: true,
    closeButton: false
  }
) {
  toast.success(getMessage(message, 'Success', 'check_circle'), options)
}

/**
 * toastInfo is used to display informational messages across the app.
 * This shows a green message toaster which is fleeting. It is dismissed
 * in 2 seconds.
 * Usage toastInfo('Table is empty')
 */
export function toastInfo(
  message: string,
  options: ToastOptions = {
    position: toast.POSITION.TOP_RIGHT as ToastPosition,
    autoClose: 2000,
    transition: Flip,
    hideProgressBar: true,
    closeButton: false
  }
) {
  toast.info(getMessage(message, 'Info', 'info'), options)
}

/*
 * Use jsonify to get the user message from the backend call.
 * For regular messages we dont need to call this. we can directly call toastError or
 * toastSuccess.
 * backendError is a string of the
 * form  "{"code":1,"user_message":"INVALID REQUEST","dev_details":"Invalid input, Alert update"}"
 */
export function jsonify(backendError: string) {
  let userMessage = ''
  try {
    const parsedBackendError: Error = JSON.parse(backendError)
    const { user_message } = parsedBackendError || ({} as Error)
    userMessage = user_message
  } catch (error) {
    userMessage = !isEmpty(backendError) ? backendError : 'Unknown Error'
  }
  return userMessage
}

/*
 * Partial Response Toast used to indicate Partial Results being displayed
 * and what actions the user needs to perform
 */

export function partialResult(
  type: any,
  options: ToastOptions = {
    position: toast.POSITION.TOP_RIGHT as ToastPosition,
    transition: Flip,
    hideProgressBar: true,
    closeButton: false
  }
) {
  let message = ''
  if (type === 'missing_records') {
    message = 'Search Criteria too broad, please refine search results'
  } else if (type === 'missing_fields') {
    message = 'Missing Analytics, try again later once analytics has completed'
  }
  toast.info(getMessage(message, 'Displaying Partial Results', 'info'), options)
}
